class FlagshipStoreNavigation extends HTMLElement {
  constructor() {
    super();
    this.links = this.querySelectorAll('[data-page-url]');
    this.contentTarget = document.querySelector('[data-content-target]');
    this.loadingBar = this.querySelector('.flagship-store-navigation__loading');
    this.cache = new Map();
    this.prefetchCache = new Map();
    this.isTransitioning = false;
  }

  connectedCallback() {
    this.setupNavigation();
    this.setupPrefetching();
    this.setupPopState();
    this.setInitialActiveState();
    this.prefetchVisibleLinks();
  }

  setInitialActiveState() {
    const currentUrl = window.location.href;
    this.updateActiveLink(currentUrl);
  }

  setupNavigation() {
    this.links.forEach(link => {
      link.addEventListener('click', (e) => this.handleNavigation(e));
    });
  }

  setupPrefetching() {
    this.links.forEach(link => {
      link.addEventListener('mouseenter', () => this.prefetchPage(link.dataset.pageUrl));
      link.addEventListener('touchstart', () => this.prefetchPage(link.dataset.pageUrl), { passive: true });
    });

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.prefetchPage(entry.target.dataset.pageUrl);
          }
        });
      }, { rootMargin: '50px' });

      this.links.forEach(link => observer.observe(link));
    }
  }

  setupPopState() {
    window.addEventListener('popstate', (e) => {
      if (e.state && e.state.url) {
        this.loadPage(e.state.url, false);
      }
    });
  }

  prefetchVisibleLinks() {
    requestIdleCallback(() => {
      this.links.forEach(link => {
        if (!link.classList.contains('flagship-store-navigation__link--active')) {
          this.prefetchPage(link.dataset.pageUrl);
        }
      });
    });
  }

  async prefetchPage(url) {
    if (this.prefetchCache.has(url) || this.cache.has(url)) {
      return;
    }

    this.prefetchCache.set(url, true);

    try {
      const response = await fetch(url, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (response.ok) {
        const html = await response.text();
        this.cache.set(url, html);
      }
    } catch (error) {
      console.error('Prefetch error:', error);
    }
  }

  async handleNavigation(e) {
    e.preventDefault();

    if (this.isTransitioning) {
      return;
    }

    const link = e.currentTarget;
    const url = link.dataset.pageUrl;

    if (link.classList.contains('flagship-store-navigation__link--active')) {
      return;
    }

    await this.loadPage(url, true);
  }

  async loadPage(url, pushState = true) {
    if (this.isTransitioning) {
      return;
    }

    this.isTransitioning = true;
    this.showLoading();

    try {
      let html;

      if (this.cache.has(url)) {
        html = this.cache.get(url);
      } else {
        const response = await fetch(url, {
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        html = await response.text();
        this.cache.set(url, html);
      }

      await this.updateContent(html, url, pushState);

    } catch (error) {
      console.error('Navigation error:', error);
      window.location.href = url;
    } finally {
      this.isTransitioning = false;
      this.hideLoading();
    }
  }

  async updateContent(html, url, pushState) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    const newContent = doc.querySelector('[data-content-target]');
    
    if (!newContent) {
      throw new Error('Content target not found in fetched page');
    }

    await this.fadeOut(this.contentTarget);

    this.contentTarget.innerHTML = newContent.innerHTML;

    this.updateActiveLink(url);

    if (pushState) {
      window.history.pushState({ url }, '', url);
    }

    this.updateDocumentMetadata(doc);

    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });

    await this.fadeIn(this.contentTarget);

    // this.reinitializeScripts();

    if (window.Shopify && window.Shopify.PaymentButton) {
      window.Shopify.PaymentButton.init();
    }
  }

  updateActiveLink(url) {
    this.links.forEach(link => {
      if (link.dataset.pageUrl === url) {
        link.classList.add('flagship-store-navigation__link--active');
        link.setAttribute('aria-current', 'page');
      } else {
        link.classList.remove('flagship-store-navigation__link--active');
        link.removeAttribute('aria-current');
      }
    });
  }

  updateDocumentMetadata(doc) {
    document.title = doc.title;

    const metaTags = ['description', 'og:title', 'og:description', 'og:image', 'twitter:title', 'twitter:description'];
    
    metaTags.forEach(name => {
      const oldMeta = document.querySelector(`meta[name="${name}"], meta[property="${name}"]`);
      const newMeta = doc.querySelector(`meta[name="${name}"], meta[property="${name}"]`);
      
      if (oldMeta && newMeta) {
        oldMeta.setAttribute('content', newMeta.getAttribute('content'));
      }
    });
  }

  reinitializeScripts() {
    const contentScripts = this.contentTarget.querySelectorAll('script');
    contentScripts.forEach(oldScript => {
      const newScript = document.createElement('script');
      Array.from(oldScript.attributes).forEach(attr => {
        newScript.setAttribute(attr.name, attr.value);
      });
      newScript.textContent = oldScript.textContent;
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });

    if (typeof window.Alpine !== 'undefined') {
      window.Alpine.initTree(this.contentTarget);
    }

    const event = new CustomEvent('flagship:content-loaded', {
      detail: { contentTarget: this.contentTarget }
    });
    document.dispatchEvent(event);
  }

  fadeOut(element) {
    return new Promise(resolve => {
      element.style.opacity = '1';
      element.style.transition = 'opacity 0.2s ease-out';
      
      requestAnimationFrame(() => {
        element.style.opacity = '0';
        setTimeout(resolve, 200);
      });
    });
  }

  fadeIn(element) {
    return new Promise(resolve => {
      element.style.opacity = '0';
      
      requestAnimationFrame(() => {
        element.style.opacity = '1';
        setTimeout(resolve, 200);
      });
    });
  }

  showLoading() {
    this.loadingBar.classList.add('flagship-store-navigation__loading--active');
  }

  hideLoading() {
    setTimeout(() => {
      this.loadingBar.classList.remove('flagship-store-navigation__loading--active');
    }, 300);
  }
}

if ('customElements' in window) {
  customElements.define('flagship-store-navigation', FlagshipStoreNavigation);
}

